import { Badge, Box, Flex, Heading, Text } from '@radix-ui/themes';
import { ErrorBoundary } from 'components/common/error-boundary';
import { HPSummarySectionCard } from 'components/sections/hitter-library/profile/summary/sections/section-card';
import useHitterProfileStore from 'components/sections/hitter-library/profile/use-hitter-profile-store';
import { t } from 'i18next';
import { HitterProfileHelper } from 'lib_ts/classes/hitter-profile.helper';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { ITotalBpChartBucket } from 'lib_ts/interfaces/session-hits/i-session-hit';
import { useMemo } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  DotProps,
  LabelList,
  LabelProps,
  ResponsiveContainer,
  Tooltip,
  XAxis,
} from 'recharts';

const ChartDot = (props: DotProps) => {
  const { cy, cx } = props;

  if (!cy) {
    return null;
  }

  return (
    <circle
      cx={cx}
      cy={cy}
      r={3}
      fill="var(--color-background)"
      stroke="var(--accent-11)"
      strokeWidth={2}
    />
  );
};

const ChartValueLabel = (props: LabelProps) => {
  const { x, y, value } = props;

  if (!x || !y) {
    return null;
  }

  if (typeof y !== 'number' || typeof x !== 'number') {
    return null;
  }

  if (!value) {
    return null;
  }

  return (
    <text
      x={x}
      y={y - 16} // move label slightly above point
      fontSize={12}
      textAnchor="middle"
    >
      {value}
    </text>
  );
};

export const HPSummaryTotalBP = () => {
  const currSummaryHits = useHitterProfileStore((s) => s.currSummaryHits);
  const hitsFilter = useHitterProfileStore((s) => s.hitsFilter);
  const loading = useHitterProfileStore((s) => s.loading);

  const { data, ticks, tickFormatter } = useMemo<{
    data: ITotalBpChartBucket[];
    ticks: number[];
    tickFormatter: (tick: number) => string;
  }>(
    () =>
      HitterProfileHelper.getTotalBpChartData({
        hits: currSummaryHits,
        period: hitsFilter.period,
      }),
    [currSummaryHits, hitsFilter.period]
  );

  const totalPitches = data.reduce((acc, b) => acc + b.count, 0);

  return (
    <ErrorBoundary identifier="HPSummaryTotalBP">
      <HPSummarySectionCard loading={loading}>
        <Flex gap={RADIX.FLEX.GAP.LG} direction="column" height="100%">
          <Box>
            <Flex justify="between" align="center">
              <Heading size="3">{t('hitters.total-bp-pitches')}</Heading>
              <Badge>
                {t('hitters.x-total-pitches-faced', { x: totalPitches })}
              </Badge>
            </Flex>
            <Text color={RADIX.COLOR.NEUTRAL}>
              {t('hitters.showing-total-bp-pitches')}
            </Text>
          </Box>

          <ResponsiveContainer width="100%" minHeight={260}>
            <AreaChart data={data} margin={{ top: 16 }}>
              <CartesianGrid vertical={false} stroke="var(--gray-6)" />
              <XAxis
                dataKey="date"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                ticks={ticks}
                tickFormatter={tickFormatter}
                color={RADIX.COLOR.SECONDARY}
                interval="preserveStartEnd"
                padding={{ left: 8, right: 8 }}
              />
              <Tooltip
                cursor={{ fill: 'var(--accent-9)', fillOpacity: 0.08 }}
                contentStyle={{
                  backgroundColor: 'var(--gray-6)',
                  border: '1px solid var(--gray-7)',
                  color: 'var(--gray-12)',
                }}
                content={({ payload, label }) => {
                  const numPitches = payload?.[0] ? payload[0].value : 0;

                  if (!label) {
                    return null;
                  }

                  return (
                    <Flex
                      style={{
                        backgroundColor: 'var(--gray-6)',
                        border: '1px solid var(--gray-7)',
                        padding: '8px 12px',
                      }}
                      direction="column"
                      gap={RADIX.FLEX.GAP.XS}
                    >
                      <Text mb="1" color={RADIX.COLOR.NEUTRAL} weight="bold">
                        {tickFormatter(label)}
                      </Text>
                      <Text color={RADIX.COLOR.NEUTRAL}>
                        {t('hitters.x-bp-pitches-faced', { x: numPitches })}
                      </Text>
                    </Flex>
                  );
                }}
              />
              <defs>
                <linearGradient
                  id="accentGradient"
                  x1="244"
                  y1="0.5"
                  x2="244"
                  y2="156"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop
                    offset="0%"
                    stopColor="#1133FF"
                    stopOpacity="0.0588235"
                  />
                  <stop
                    offset="100%"
                    stopColor="#3566FF"
                    stopOpacity="0.341176"
                  />
                </linearGradient>
              </defs>
              <Area
                dataKey="count"
                type="linear"
                fill="url(#accentGradient)"
                stroke="var(--accent-a9, #3E63DD)"
                activeDot={<ChartDot />}
              >
                <LabelList
                  dataKey="count"
                  position="top"
                  content={<ChartValueLabel />}
                />
              </Area>
            </AreaChart>
          </ResponsiveContainer>
        </Flex>
      </HPSummarySectionCard>
    </ErrorBoundary>
  );
};
